/**
 * Before/after test chat on an unapplied workflow (AIT-146 / W4-07).
 *
 * The turn runs through the connector's internal contour, which is why this whole
 * feature is dev-gated — see `workflowTestChatEnabled` in services/api.
 *
 * Nothing here assumes a response shape it has not seen. The seat and turn payloads were
 * described in AIT-126 but never called from a browser before, so every reader accepts
 * the plausible spellings, and keeps `raw` so an unrecognised shape can be shown to the
 * user instead of silently becoming an empty chat bubble.
 */

export type ChatSide = 'before' | 'after';

export interface Seat {
  seatId: string;
  userId: string;
  /** The seat's `ResolvedUserState` — `{user_state, state}` — plus its routing. */
  state?: unknown;
  routing?: unknown;
  raw: unknown;
}

export interface TurnUsage {
  tokens?: number;
  cost?: number;
}

export interface TurnResult {
  text: string;
  /** Counters the full production turn reports; absent in the shortened one. */
  events: number;
  patchOps: number;
  intent: { kind?: string; wouldMove?: string; executed?: boolean } | null;
  /** What the turn cost. A full production turn is not cheap — worth showing. */
  usage: TurnUsage | null;
  raw: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Some connector routes answer bare, others in the `{success, data}` envelope. */
export function unwrap(payload: unknown): unknown {
  if (isRecord(payload) && 'success' in payload && 'data' in payload) return payload.data;
  return payload;
}

function firstString(source: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return undefined;
}

function countOf(value: unknown): number {
  if (Array.isArray(value)) return value.length;
  if (isRecord(value)) return Object.keys(value).length;
  return 0;
}

/**
 * `seat_id` equals the virtual user's id in AI Psy, but which of the two the payload
 * spells is not something to guess — either is accepted, and each falls back to the other.
 */
export function parseSeat(payload: unknown): Seat | null {
  const data = unwrap(payload);
  if (!isRecord(data)) return null;
  const nested = isRecord(data.seat) ? data.seat : data;

  const seatId = firstString(nested, ['seat_id', 'seatId', 'id', 'user_id', 'userId']);
  const userId = firstString(nested, ['user_id', 'userId', 'seat_id', 'seatId', 'id']);
  if (!seatId || !userId) return null;

  return {
    seatId,
    userId,
    state: nested.state ?? (isRecord(data) ? data.state : undefined),
    routing: nested.routing ?? (isRecord(data) ? data.routing : undefined),
    raw: payload,
  };
}

/**
 * The body's `state` is a `ResolvedUserState`, not a state definition: it carries
 * `user_state` (the position — user, workflow version, current state, phase) beside
 * `state` (the definition the turn runs on). Sending a bare definition answers 422 with
 * every leaf of both halves reported missing, because each half defaults to `{}`.
 *
 * The seat already holds a valid position, so it is the base and only the definition and
 * the state we are testing are swapped in. `phase` in particular is the product's own
 * enum — taking it from the seat beats guessing a value.
 */
export function buildResolvedUserState(params: {
  seat: Seat;
  stateId: string;
  /** The before or after definition of that state. */
  definition: unknown;
  /** From the job's `base_revision`; the seat's own value wins when it has one. */
  workflowVersionId?: string;
}): Record<string, unknown> {
  const { seat, stateId, definition, workflowVersionId } = params;
  const base = isRecord(seat.state) ? seat.state : {};
  const baseUserState = isRecord(base.user_state) ? base.user_state : {};

  return {
    ...base,
    user_state: {
      ...baseUserState,
      user_id: baseUserState.user_id ?? seat.userId,
      ...(baseUserState.workflow_version_id ?? workflowVersionId
        ? { workflow_version_id: baseUserState.workflow_version_id ?? workflowVersionId }
        : {}),
      // The turn must run on the state under comparison, not on wherever the seat began.
      current_state_id: stateId,
    },
    state: definition,
  };
}

/** Fields the server requires and neither the seat nor the graph supplied. */
export function missingPositionFields(resolved: Record<string, unknown>): string[] {
  const userState = isRecord(resolved.user_state) ? resolved.user_state : {};
  return ['user_id', 'workflow_version_id', 'current_state_id', 'phase'].filter(
    (key) => userState[key] === undefined || userState[key] === null || userState[key] === ''
  );
}

function numberAt(source: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
}

const TOKEN_KEYS = ['total_tokens', 'tokens'];
const COST_KEYS = ['total_cost', 'cost'];

/**
 * Usage may be reported flat or split into `reply_barrier` / `after_reply` — the second
 * of which does not count towards production at all (AIT-126). Without a flat total the
 * nested ones are summed rather than reported as nothing.
 */
export function parseUsage(value: unknown): TurnUsage | null {
  if (!isRecord(value)) return null;

  let tokens = numberAt(value, TOKEN_KEYS);
  let cost = numberAt(value, COST_KEYS);

  if (tokens === undefined || cost === undefined) {
    let nestedTokens = 0;
    let nestedCost = 0;
    let sawTokens = false;
    let sawCost = false;
    Object.values(value).forEach((entry) => {
      if (!isRecord(entry)) return;
      const entryTokens = numberAt(entry, TOKEN_KEYS);
      const entryCost = numberAt(entry, COST_KEYS);
      if (entryTokens !== undefined) {
        nestedTokens += entryTokens;
        sawTokens = true;
      }
      if (entryCost !== undefined) {
        nestedCost += entryCost;
        sawCost = true;
      }
    });
    if (tokens === undefined && sawTokens) tokens = nestedTokens;
    if (cost === undefined && sawCost) cost = nestedCost;
  }

  if (tokens === undefined && cost === undefined) return null;
  return { ...(tokens === undefined ? {} : { tokens }), ...(cost === undefined ? {} : { cost }) };
}

/** Keys the reply text itself lives under — already shown as the bubble, so not repeated. */
export const REPLY_TEXT_KEYS = ['content', 'reply', 'message', 'answer', 'text', 'response', 'final_message'];

export function parseTurn(payload: unknown): TurnResult {
  const data = unwrap(payload);
  const source = isRecord(data) ? data : {};
  const text = firstString(source, REPLY_TEXT_KEYS) ?? '';

  const rawIntent = isRecord(source.intent) ? source.intent : null;
  const intent = rawIntent
    ? {
        kind: typeof rawIntent.kind === 'string' ? rawIntent.kind : firstString(rawIntent, ['type', 'name', 'action']),
        wouldMove: firstString(rawIntent, ['would_move_to', 'target_state_id', 'target', 'next_state_id']),
        executed: typeof rawIntent.executed === 'boolean' ? rawIntent.executed : undefined,
      }
    : null;

  return {
    text,
    events: countOf(source.turn_events),
    patchOps: countOf(source.state_patch),
    intent,
    usage: parseUsage(source.usage),
    raw: payload,
  };
}

/** A reply the reader can act on, or an explicit statement that the shape was unfamiliar. */
export function turnText(turn: TurnResult): string {
  if (turn.text) return turn.text;
  return 'The turn returned no recognisable reply text. Open the raw response below — the field may be spelled differently than any this build knows.';
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  turn?: TurnResult;
}

/** This route spells the roles `human`/`ai`, unlike `/nlt/chats`, which uses user/assistant. */
export function toDialog(messages: ChatMessage[]): Array<{ type: 'human' | 'ai'; content: string }> {
  return messages.map((message) => ({
    type: message.role === 'user' ? 'human' : 'ai',
    content: message.content,
  }));
}

export function errorText(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const data = response?.data;
  const detail =
    (isRecord(data) && (firstString(data, ['message', 'detail', 'error']) ?? undefined)) ||
    (isRecord(data) && isRecord(data.error) ? firstString(data.error, ['message', 'detail']) : undefined);
  const status = response?.status;
  const base = detail || (error as Error)?.message || 'The turn failed.';
  return status ? `${status} — ${base}` : base;
}
